// A thin wrapper around the WebSocket to a room's Durable Object. It knows
// nothing about reconnect strategy or backoff — that is the call state
// machine's job (packages/core's transition()) driven from useCallStore —
// this class only connects, sends, and reports what it heard.

import type { ClientMessage, RoomMessage } from "@breeze/core";

export type RoomKind = "personal" | "once";

export interface RoomSocketHandlers {
  readonly onMessage: (message: RoomMessage) => void;
  readonly onOpen?: () => void;
  readonly onClose?: (event: CloseEvent) => void;
  readonly onError?: (event: Event) => void;
}

export function roomWsUrl(
  kind: RoomKind,
  slug: string,
  name: string,
  deviceId: string,
  rejoinToken?: string,
  userKey?: string,
  move?: boolean,
  /** More query parameters, such as `listen=1` from the Listen page. The
   * Worker strips every identity parameter first, so a value here cannot
   * grant anything the key does not. */
  extra?: Readonly<Record<string, string>>,
): string {
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  const params = new URLSearchParams({ name, deviceId });
  // The Worker checks the key and strips it; only the room's own owner comes
  // out of that as host (worker/src/index.ts `routeRoom`).
  if (userKey) params.set("key", userKey);
  // Present on a reconnect only (see worker/src/room.ts's acceptCall) — a
  // fresh first join has never held a rejoin token and omits it, same as
  // before.
  if (rejoinToken) params.set("rejoinToken", rejoinToken);
  // Take this account's seat over from another device instead of joining as
  // a second person (worker/src/room.ts `takeOverSeat`).
  if (move) params.set("move", "1");
  for (const [name, value] of Object.entries(extra ?? {})) params.set(name, value);
  return `${proto}//${window.location.host}/api/rooms/${kind}/${encodeURIComponent(slug)}/ws?${params.toString()}`;
}

/** How often a socket tells the Room it is still there. The Room closes one
 * that stays quiet for 90 s (worker/src/room.ts `STALE_SOCKET_MS`). */
export const PING_MS = 25_000;
/** While the live assistant is on: often enough that the Room never goes idle
 * long enough to be evicted, which would turn the assistant off. */
export const PING_ASSISTANT_MS = 8_000;

export class RoomSocket {
  private ws: WebSocket | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private pingMs = PING_MS;

  constructor(
    private readonly url: string,
    private readonly handlers: RoomSocketHandlers,
  ) {}

  connect(): void {
    const ws = new WebSocket(this.url);
    this.ws = ws;
    ws.addEventListener("open", () => {
      this.startPings();
      this.handlers.onOpen?.();
    });
    ws.addEventListener("message", (event) => {
      if (typeof event.data !== "string") return;
      let parsed: RoomMessage;
      try {
        parsed = JSON.parse(event.data) as RoomMessage;
      } catch {
        return;
      }
      this.handlers.onMessage(parsed);
    });
    ws.addEventListener("close", (event) => {
      if (this.ws === ws) this.stopPings();
      this.handlers.onClose?.(event);
    });
    ws.addEventListener("error", (event) => this.handlers.onError?.(event));
  }

  send(message: ClientMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }

  /** A message outside the shared protocol (the drop log's `dropReport`,
   * worker/src/drops.ts), which a room that does not know it ignores. */
  sendLoose(message: Readonly<Record<string, unknown>>): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(message));
  }

  /** Raw audio for the live assistant: 16 kHz mono 16-bit PCM, the only
   * binary a client ever sends. */
  sendBinary(data: ArrayBuffer): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(data);
  }

  /** Ping faster or slower from now on. */
  setPingMs(ms: number): void {
    if (ms === this.pingMs) return;
    this.pingMs = ms;
    if (this.pingTimer) this.startPings();
  }

  private startPings(): void {
    this.stopPings();
    this.pingTimer = setInterval(() => this.send({ type: "ping" }), this.pingMs);
  }

  private stopPings(): void {
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.pingTimer = null;
  }

  /** An explicit leave: 4000 is the code worker/src/room.ts's
   * EXPLICIT_LEAVE_CODE, which is how the server tells "they left" apart
   * from "the network dropped" (BRIEF 7.2 — leave never asks for
   * confirmation and never shows a reconnect state on the leaver's own
   * screen). */
  leave(): void {
    this.send({ type: "leave" });
    this.stopPings();
    this.ws?.close(4000, "left");
  }

  close(): void {
    this.stopPings();
    this.ws?.close();
  }
}
